import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const navItems = [
  { to: "/", label: "Home", testid: "home" },
  { to: "/shop", label: "Shop", testid: "shop" },
  { to: "/buy", label: "Sell your cards", testid: "sell-your-cards" },
  { to: "/trade", label: "Trade", testid: "trade" },
  { to: "/request-cards", label: "Request", testid: "request" },
  { to: "/shows", label: "Shows", testid: "shows" },
];

export default function NavBar() {
  const { user, isAdmin, isCustomer, logout } = useAuth();
  const { count } = useCart();

  return (
    <header
      data-testid="site-navbar"
      className="backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60 sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 font-black text-white tracking-tight"
        >
          <img
            src="/brand/logo-red.png"
            alt="Washed Up Trainers"
            className="h-10 sm:h-12 w-auto"
          />
          <span className="hidden sm:flex flex-col leading-none font-[Outfit]">
            <span className="text-[10px] tracking-[0.25em] text-slate-400 font-mono uppercase">
              Washed Up Trainers
            </span>
            <span className="text-lg">
              THE <span className="text-yellow-400">ARENA</span>
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.testid}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-md transition-colors ${
                  isActive
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-red-600/90 text-white hover:bg-red-700"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/checkout"
            data-testid="nav-cart"
            aria-label="Cart"
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-md bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 transition-colors"
          >
            <ShoppingCart className="w-4 h-4" />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-bold font-mono"
              >
                {count}
              </span>
            )}
          </Link>
          {isAdmin && (
            <>
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="hidden sm:inline-flex bg-slate-800 text-white border border-slate-700 px-3 py-1.5 rounded-md text-sm hover:bg-slate-700"
              >
                Admin
              </Link>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="text-sm text-slate-400 hover:text-white px-2"
              >
                Logout
              </button>
            </>
          )}
          {isCustomer && (
            <>
              <Link
                to="/account"
                data-testid="nav-account"
                className="inline-flex items-center gap-1.5 bg-yellow-400 text-slate-950 font-bold px-3 py-1.5 rounded-md text-sm hover:bg-yellow-300"
              >
                <span className="hidden sm:inline">My account</span>
                <span className="sm:hidden">Account</span>
              </Link>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="text-sm text-slate-400 hover:text-white px-2"
              >
                Logout
              </button>
            </>
          )}
          {!user && (
            <Link
              to="/signin"
              data-testid="nav-signin"
              className="hidden sm:inline-flex bg-yellow-400 text-slate-950 font-bold px-3 py-1.5 rounded-md text-sm hover:bg-yellow-300"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
      {/* Mobile nav */}
      <nav className="md:hidden flex items-center gap-1 px-4 pb-3 overflow-x-auto">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`nav-mobile-${n.testid}`}
            className={({ isActive }) =>
              `px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full whitespace-nowrap ${
                isActive
                  ? "bg-red-700 text-white"
                  : "bg-red-600 text-white"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
